import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Ticker from "../components/Ticker";
import Footer from "../components/Footer";
import { ArticlesAPI, normalizeArticle } from "../lib/api";
import { getCategory, MAX_PHOTO } from "../mock";
import { ArticleCardMedium, timeAgo } from "../components/ArticleCards";
import { Clock, Headphones, Bookmark, Share2, Play, Pause, Volume2, ChevronLeft, FileText, Loader2, Eye, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Byline, ArticleShareBar, CommentsThread } from "../components/ArticleExtras";
import { ArticleStatsAPI } from "../lib/api";
import { useAuth } from "../lib/auth";
import PodcastQueue from "../components/PodcastQueue";
import PodcastPlatformStrip from "../components/PodcastPlatformStrip";
import TierRibbon from "../components/TierRibbon";
import { cropUnsplash } from "../lib/api";
import SmartImage from "../components/SmartImage";

const AudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const { user, requireAuth } = useAuth();

  const toggle = () => {
    if (!user) {
      requireAuth("audio");
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause(); else a.play();
    setPlaying(!playing);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-[#0B1F33] text-[#D7DEE5] p-4 md:p-5 flex items-center gap-4 my-8">
      <button onClick={toggle} className="w-12 h-12 rounded-full bg-[#B8E9F1] hover:bg-[#9FDDE7] flex items-center justify-center shrink-0 transition-colors">
        {playing ? <Pause className="w-5 h-5 text-[#0B1F33]" /> : <Play className="w-5 h-5 text-[#0B1F33] ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">
          <Headphones className="w-3 h-3" /> Listen to this article
        </div>
        <div className="mt-1 text-[13px] truncate">{title}</div>
        <div className="mt-2 relative h-1 bg-[#132f57] rounded-full overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#B8E9F1]" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-[10px] font-mono-tick text-[#D7DEE5]/60">
          <span>{fmt(progress)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
      <Volume2 className="w-4 h-4 text-[#D7DEE5]/60 hidden md:block" />
      <audio
        key={src}
        ref={audioRef}
        src={src}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
};

const BodyRenderer = ({ article }) => {
  const type = article.bodyType || "text";
  if (article.bodyFile && (type === "pdf")) {
    return (
      <div className="my-8">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> PDF Source</div>
          <a href={article.bodyFile} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-wider text-[#0891B2] gf-link">Open in new tab</a>
        </div>
        <iframe src={article.bodyFile} title={article.title} className="w-full h-[80vh] border border-neutral-300" />
      </div>
    );
  }
  if (type === "html" && article.body) {
    return <div className="gf-prose mt-8" dangerouslySetInnerHTML={{ __html: article.body }} />;
  }
  const text = article.body || "";
  return (
    <div className="gf-prose mt-8">
      {text.split(/\n\n+/).map((p, i) => (
        <p key={i} className={i === 0 ? 'gf-dropcap' : ''}>{p}</p>
      ))}
    </div>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, requireAuth, showPaywall, signIn } = useAuth();

  useEffect(() => {
    setLoading(true);
    ArticlesAPI.get(slug)
      .then((data) => {
        const a = normalizeArticle(data);
        setArticle(a);
        // Fire the page-view beacon (public, silent-on-error)
        if (a?.id) ArticleStatsAPI.view(a.id);
        // If backend returned a locked article, trigger the paywall:
        //  - signed-out reader → sign-in wall (free trial CTA)
        //  - signed-in reader  → 2-card tier picker (Premium / Professional)
        // VexGlobalLinks content is always free and never carries `_locked`.
        if (a?._locked) {
          const tier = (a.readTier === "professional") ? "professional" : "premium";
          if (user) showPaywall(tier);
          else requireAuth(tier);
        }
        return ArticlesAPI.list({ category: a.category, limit: 6 });
      })
      .then((list) => setRelated(list.map(normalizeArticle)))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug, requireAuth, showPaywall, user]);

  // --- SEO: inject <title>, meta description, canonical, and JSON-LD ---
  // JS-capable crawlers (Googlebot, Bingbot, modern ChatGPT browsing) pick this
  // up on client-render. Non-JS bots use /api/prerender/article/{slug} instead
  // (listed in the sitemap).
  useEffect(() => {
    if (!article) return;
    const site = window.location.origin;
    const canonical = `${site}/article/${article.slug}`;
    const description = (article.dek || (article.body || "").replace(/<[^>]+>/g, " "))
      .toString()
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 280);

    document.title = `${article.title} — Vexent Wire`;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", description);
    setMeta("name", "robots", "index, follow, max-snippet:-1, max-image-preview:large");
    setMeta("property", "og:type", "article");
    setMeta("property", "og:title", article.title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    if (article.image) setMeta("property", "og:image", article.image);
    setMeta("name", "twitter:title", article.title);
    setMeta("name", "twitter:description", description);

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD NewsArticle schema — tuned for Google News + rich results
    // eligibility. Required fields Google enforces:
    //   • headline (≤110 chars ideally)
    //   • image (absolute URL, min 1200px wide — we have 1600w AVIF+JPG)
    //   • datePublished + dateModified (ISO 8601)
    //   • author (with @type)
    //   • publisher (NewsMediaOrganization + logo ImageObject)
    //   • mainEntityOfPage
    // Optional but boost eligibility:
    //   • isAccessibleForFree (false for gated tiers → prompts subscription
    //     structured data eligibility on Google side)
    //   • articleSection, keywords, wordCount
    const absImage = article.image
      ? (article.image.startsWith("http") ? article.image : `${site}${article.image}`)
      : `${site}/assets/icon-512.png`;
    const plainBody = (article.body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const wordCount = plainBody ? plainBody.split(" ").length : 0;
    const isFree = (article.readTier || "standard") === "standard";
    const ld = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      description,
      image: [absImage],
      datePublished: article.publishedAt,
      dateModified: article.updatedAt || article.publishedAt,
      author: [{
        "@type": "Person",
        name: article.author || "Max Fischer",
        url: `${site}/masthead`,
      }],
      publisher: {
        "@type": "NewsMediaOrganization",
        name: "Vexent Wire",
        url: site,
        logo: {
          "@type": "ImageObject",
          url: `${site}/assets/icon-512.png`,
          width: 512,
          height: 512,
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      articleSection: article.category,
      keywords: Array.isArray(article.tags) ? article.tags.join(", ") : "",
      wordCount,
      inLanguage: "en-US",
      isAccessibleForFree: isFree,
      ...(isFree ? {} : {
        // Signals paid content to Google so it doesn't flag cloaking.
        hasPart: {
          "@type": "WebPageElement",
          isAccessibleForFree: false,
          cssSelector: ".paywall-gated",
        },
      }),
    };
    let script = document.getElementById("gf-article-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "gf-article-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    // Breadcrumb schema — helps Google display "Home › Category › Article"
    // in search snippets.
    const bc = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: site },
        ...(article.category ? [{
          "@type": "ListItem",
          position: 2,
          name: article.category,
          item: `${site}/?category=${encodeURIComponent(article.category)}`,
        }] : []),
        {
          "@type": "ListItem",
          position: article.category ? 3 : 2,
          name: article.title,
          item: canonical,
        },
      ],
    };
    let bcScript = document.getElementById("gf-article-breadcrumb");
    if (!bcScript) {
      bcScript = document.createElement("script");
      bcScript.type = "application/ld+json";
      bcScript.id = "gf-article-breadcrumb";
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify(bc);

    return () => {
      // Reset title on unmount; leave meta tags in place so subsequent
      // pages overwrite them cleanly.
      document.title = "Vexent Wire — Independent Financial Research & Commentary";
    };
  }, [article]);

  const cat = useMemo(() => (article ? getCategory(article.category) : null), [article]);

  if (loading) {
    return (
      <div>
        <Ticker /><Header />
        <div className="max-w-[900px] mx-auto px-6 py-32 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-6 h-6 animate-spin mr-3 text-[#0891B2]" /> Loading article…
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div>
        <Ticker /><Header />
        <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <h1 className="font-serif-display text-4xl font-bold text-[#0B1F33]">Article Not Found</h1>
          <p className="mt-2 text-neutral-600">The piece you're looking for may have moved.</p>
          <Link to="/" className="inline-block mt-6 text-[#0891B2] gf-link uppercase tracking-widest text-sm">Return home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relList = related.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div>
      <Ticker />
      <Header />

      <article className="max-w-[1400px] mx-auto px-6 py-10">
        {article.specialReport && (
          <div
            data-testid="special-report-banner"
            className="mb-6 flex items-center gap-3 bg-[#7a1c1c] text-[#FFFFFF] px-4 py-2.5 border-l-4 border-[#B8E9F1]"
          >
            <span className="inline-block w-2 h-2 bg-[#B8E9F1] rounded-full animate-pulse" />
            <span className="font-condensed uppercase tracking-[0.28em] text-[11px] font-bold">
              Special Report &middot; Time-Sensitive
            </span>
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Link to={`/section/${cat.slug}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: cat.color }}>
            <ChevronLeft className="w-3.5 h-3.5" /> {cat.label}
          </Link>
          <TierRibbon tier={article.readTier} size="xxs" className="ml-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-4">
          <div className="lg:col-span-8 lg:col-start-3">
            <h1 className="font-serif-display text-[36px] md:text-[54px] font-bold text-[#0B1F33] leading-[1.05]">{article.title}</h1>
            {article.dek && <p className="mt-4 text-[19px] md:text-[21px] text-neutral-700 font-serif-display italic leading-relaxed">{article.dek}</p>}

            <div className="mt-6 pb-6 border-b border-neutral-300 flex flex-wrap items-center gap-4">
              <Byline article={article} avatarSrc={MAX_PHOTO} />
              <div className="h-8 w-px bg-neutral-300 hidden sm:block" />
              <div className="flex items-center gap-3 text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-mono-tick">
                {typeof article.views === "number" && (
                  <span className="inline-flex items-center gap-1" data-testid="article-view-count">
                    <Eye className="w-3 h-3" /> {(article.views || 0).toLocaleString()}
                  </span>
                )}
                {article.audio && (
                  <span className="inline-flex items-center gap-1">
                    <Headphones className="w-3 h-3" /> {(article.plays || 0).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <ArticleShareBar article={article} />
              </div>
            </div>

            {article._locked ? (
              <div className="mt-8" data-testid="premium-read-gate">
                {article.image && (
                  <figure className="mb-8">
                    <SmartImage
                      src={cropUnsplash(article.image, { w: 1920, h: 1080 })}
                      alt={article.title}
                      sizes="(min-width: 1024px) 66vw, 100vw"
                      eager
                      className="w-full aspect-[16/9] object-cover"
                    />
                  </figure>
                )}
                <div className="border-2 border-[#B8E9F1] bg-white p-8 md:p-10 text-center">
                  <Lock className="w-8 h-8 text-[#0891B2] mx-auto mb-4" />
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">
                    {article.readTier === "professional" ? "Professional Research" : "Premium Research"}
                  </div>
                  <h2 className="mt-3 font-serif-display text-[26px] md:text-[32px] font-bold text-[#0B1F33] leading-tight max-w-[520px] mx-auto">
                    {user
                      ? "Pick a plan to continue reading"
                      : "Sign in — free — to continue reading"}
                  </h2>
                  <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed max-w-[520px] mx-auto">
                    {user
                      ? "This piece is reserved for paid subscribers. Start a 30-day free trial — cancel anytime before it ends and you won't be charged."
                      : "This piece is reserved for Premium subscribers. Start a 30-day free trial — card required at signup, cancel anytime before the trial ends and you won't be charged."}
                  </p>
                  <button
                    onClick={() => {
                      const tier = (article.readTier === "professional") ? "professional" : "premium";
                      if (user) showPaywall(tier);
                      else requireAuth(tier);
                    }}
                    data-testid="premium-gate-signin"
                    className="mt-6 inline-flex items-center gap-2 bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] px-6 py-3 text-[12px] tracking-[0.28em] uppercase font-condensed font-bold transition-colors"
                  >
                    {user ? "See Plans" : "Continue with Google — Free"}
                  </button>
                </div>
              </div>
            ) : (
            <>
            {article.image && (
              <figure className="mt-8">
                <SmartImage
                  src={article.image}
                  alt={article.title}
                  sizes="(min-width: 1024px) 66vw, 100vw"
                  eager
                  className="w-full aspect-[16/9] object-cover"
                />
                <figcaption className="mt-2 text-[11px] uppercase tracking-wider text-neutral-500">Photo: Editorial · Vexent Wire Archive</figcaption>
              </figure>
            )}

            {article.audio && <AudioPlayer src={article.audio} title={article.title} />}

            <PodcastPlatformStrip article={article} />

            {article.podcastAudio && <PodcastQueue article={article} />}

            <PremiumPdfCTA slug={article.slug} />

            {article.video && (
              <div className="my-8">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold mb-2 inline-flex items-center gap-1.5">Video Companion</div>
                <div className="relative aspect-video bg-black">
                  {article.video.includes('youtube.com/embed') || article.video.includes('vimeo.com') ? (
                    <iframe src={article.video} title="Video" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <video src={article.video} controls className="absolute inset-0 w-full h-full" />
                  )}
                </div>
              </div>
            )}

            <BodyRenderer article={article} />
            </>
            )}

            {/* Retention/archive metadata kept internal-only — no public disclosure */}

            {article.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-semibold mr-2">Tags</span>
                {article.tags.map((t) => (
                  <span key={t} className="text-[11px] uppercase tracking-wider px-2 py-1 border border-neutral-300 text-neutral-700 hover:border-[#B8E9F1] hover:text-[#0891B2] transition-colors cursor-pointer">{t}</span>
                ))}
              </div>
            )}

            <div className="mt-10 p-6 bg-white border border-neutral-300">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0891B2] font-semibold">Author</div>
              <div className="mt-3 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-[#0B1F33] shrink-0">
                  <img src={MAX_PHOTO} alt={article.author} className="w-full h-full object-cover" style={{ objectPosition: "center 15%" }} />
                </div>
                <div className="font-serif-display text-[20px] font-bold text-[#0B1F33]">{article.author}</div>
              </div>
            </div>

            <CommentsThread articleId={article.id} />
          </div>
        </div>

        {relList.length > 0 && (
          <section className="mt-16" data-testid="related-articles">
            <div className="mb-6">
              <div className="inline-block relative pb-2 border-b-[3px] border-[#B8E9F1]">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">More in {cat.label}</div>
                <h2 className="mt-1 font-condensed uppercase text-[24px] font-bold text-[#0B1F33] tracking-tight">Related Reading</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relList.map((a) => <ArticleCardMedium key={a.id} a={a} />)}
            </div>
          </section>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default ArticlePage;
